import React, { useCallback, useState } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';

type PasswordInputProps = {
  id: string;
  name: string;
  type: 'password';
  label: string;
  placeholder: string;
  shouldTranslate: boolean;
  value: string;
  error: string;
  isValid: boolean;
  emitChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showPasswordRequirements?: boolean;
};

import commonUtils from '@/school-management/utils/common';
import { cognitoService } from '@/services/cognito';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

const UsePasswordChange = () => {
  const { t } = useAdminTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [hasPasswordUpdated, setHasPasswordUpdated] = useState(false);
  const { user } = useAppSelector(authSelector);
  const [showPasswordInfo, setShowPasswordInfo] = useState(false);
  const [hasNewPasswordValue, setHasNewPasswordValue] = useState(false);

  const onChangePasswordFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;

    setChangePasswordFields(prevState => {
      const updatedState = [...prevState];
      const fieldIndex = updatedState.findIndex(i => i.name === name);

      updatedState[fieldIndex].value = value.trim();
      updatedState[fieldIndex].isValid = false;
      updatedState[fieldIndex].error = '';

      if (name === 'newPassword') {
        setHasNewPasswordValue(updatedState[fieldIndex].value.length > 0);
        setShowPasswordInfo(false);
      }

      return updatedState;
    });
  };

  const initialChangePasswordFields = [
    {
      id: 'current_password',
      name: 'currentPassword',
      type: 'password',
      label: 'current_password',
      placeholder: 'current_password',
      shouldTranslate: true,
      value: '',
      error: '',
      isValid: false,
      emitChange: onChangePasswordFieldChange,
    },
    {
      id: 'new_password',
      name: 'newPassword',
      type: 'password',
      label: 'new_password',
      placeholder: 'new_password',
      shouldTranslate: true,
      value: '',
      error: '',
      isValid: false,
      showPasswordRequirements: true,
      emitChange: onChangePasswordFieldChange,
    },
    {
      id: 'new_password_verification',
      name: 'newPasswordVerification',
      type: 'password',
      label: 'new_password_verification',
      placeholder: 'confirmPassword',
      shouldTranslate: true,
      value: '',
      error: '',
      isValid: false,
      emitChange: onChangePasswordFieldChange,
    },
  ] as PasswordInputProps[];

  const [changePasswordFields, setChangePasswordFields] = useState<PasswordInputProps[]>(
    initialChangePasswordFields
  );

  const runValidation = (state: PasswordInputProps[]): PasswordInputProps[] => {
    const updatedState = [...state];
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.{}\[\]()?\-\"!@#%&/\\,><':;|_~`+=])[A-Za-z\d\^$*.{}\[\]()?\-\"!@#%&/\\,><':;|_~`+=]{8,}$/;

    for (let i = 0; i < updatedState.length; i++) {
      const { id, value } = updatedState[i];

      const isCurrentPassword = id === 'current_password';
      const isNewPasswordField = id === 'new_password';
      const isConfirmNewPasswordField = id === 'new_password_verification';

      const newPasswordIndex = updatedState.findIndex(field => field.id === 'new_password');
      const confirmNewPasswordIndex = updatedState.findIndex(
        field => field.id === 'new_password_verification'
      );
      const newPasswordValue = updatedState[newPasswordIndex].value;
      const confirmNewPasswordValue = updatedState[confirmNewPasswordIndex].value;

      if (isCurrentPassword || isNewPasswordField) {
        if (!value) {
          updatedState[i].isValid = false;
          updatedState[i].error = t('dynamicForm.validation.required');
        } else {
          const isPasswordValid = passwordRegex.test(value);
          updatedState[i].isValid = isPasswordValid;
          updatedState[i].error = isPasswordValid ? '' : t('changePassword.invalidPassword');
        }
      }

      if (isConfirmNewPasswordField) {
        if (!confirmNewPasswordValue) {
          updatedState[i].isValid = false;
          updatedState[i].error = t('dynamicForm.validation.required');
        } else {
          const isNewPasswordValid = passwordRegex.test(newPasswordValue ?? '');
          if (isNewPasswordValid) {
            const isIdentical = confirmNewPasswordValue === newPasswordValue;
            updatedState[i].isValid = isIdentical;
            updatedState[i].error = isIdentical ? '' : t('changePassword.passwordsDontMatch');
          } else {
            updatedState[i].isValid = false;
            updatedState[i].error = '';
          }
        }
      }
    }

    return updatedState;
  };

  const validateOnly = (): boolean => {
    let next = changePasswordFields;
    next = runValidation(next);
    setChangePasswordFields(next);
    return next.every(f => f.isValid);
  };

  const onChangePasswordSubmit = async (e?: React.FormEvent<HTMLFormElement>): Promise<boolean> => {
    if (e) e.preventDefault();

    // 1) validate
    const isValid = validateOnly();
    if (!isValid) {
      setHasPasswordUpdated(false);
      return false;
    }

    // 2) proceed to API
    try {
      setIsLoading(true);
      const currentPasswordField =
        changePasswordFields.find(field => field.id === 'current_password')?.value ?? '';

      const newPasswordField = changePasswordFields.find(i => i.id === 'new_password')?.value ?? '';

      const userEmail = localStorage.getItem('userEmail') ?? '';

      await cognitoService.updatePassword(userEmail, currentPasswordField, newPasswordField);

      commonUtils.showToast(t('loginPage.changePasswordForm.passwordChangedSuccessfully'));
      setHasPasswordUpdated(true);
      setChangePasswordFields(prevState =>
        prevState.map(field => ({ ...field, value: '', error: '', isValid: false }))
      );
      return true;
    } catch (error: any) {
      const errCode = error as string;
      const errMsg = errCode === 'NotAuthorizedException' ? 'invalidUsernameOrPassword' : errCode;
      commonUtils.showToast(t(errMsg));
      setHasPasswordUpdated(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetFields = useCallback(() => {
    setChangePasswordFields(prevState =>
      prevState.map(field => ({ ...field, value: '', error: '', isValid: false }))
    );
    setShowPasswordInfo(false);
    setHasNewPasswordValue(false);
  }, []);

  return {
    isLoading,
    hasPasswordUpdated,
    changePasswordFields,
    onChangePasswordSubmit,
    validateOnly,
    resetFields,
    showPasswordInfo,
    onNewPasswordBlur: () => {},
    onTogglePasswordInfo: () => setShowPasswordInfo(prev => !prev),
    onHidePasswordInfo: () => setShowPasswordInfo(false),
    hasNewPasswordValue,
  };
};

export default UsePasswordChange;
